import React from "react";
import { motion } from "framer-motion";
import JobStatusChip from "./JobStatusChip";
import ActionButtons from "../../../components/PageUI/ActionButtons";
import { formatNumber } from "../../../services/analyticsService";
import { jobButtonsConfig } from "../constants/notificationConstants";
import {
  CalendarIcon,
  UserGroupIcon,
  BellIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  CursorClickIcon,
} from "@heroicons/react/outline";

const JobCard = ({ job, onViewDetails, onCancel, onRetry }) => {
  const handleAction = (action) => {
    switch (action) {
      case "view":
        onViewDetails(job);
        break;
      case "cancel":
        onCancel(job._id);
        break;
      case "retry":
        onRetry(job._id);
        break;
      default:
        break;
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const targetDate = new Date(date);
    const diffInHours = Math.abs(now - targetDate) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return targetDate.toLocaleString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return targetDate.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getProgressPercentage = () => {
    if (!job.stats?.total || job.stats.total === 0) return 0;
    return Math.round((job.stats.sent / job.stats.total) * 100);
  };

  const getStatusMessage = () => {
    const status = job.status?.toLowerCase();
    const total = job.stats?.total || 0;
    const sent = job.stats?.sent || 0;
    const delivered = job.stats?.delivered || 0;
    const failed = job.stats?.failed || 0;

    switch (status) {
      case "pending":
        return job.scheduleAt
          ? `Scheduled to send to ${formatNumber(total)} people`
          : `Ready to send to ${formatNumber(total)} people`;
      case "processing":
      case "sending":
        return `Sending to ${formatNumber(total)} people...`;
      case "sent":
      case "completed":
        if (delivered > 0) {
          return `Successfully delivered to ${formatNumber(delivered)} people`;
        }
        return `Sent to ${formatNumber(sent)} people`;
      case "failed":
        return `Failed to send to ${formatNumber(failed)} people`;
      case "cancelled":
        return "This message was cancelled";
      default:
        return `Message to ${formatNumber(total)} people`;
    }
  };

  const getMessageTitle = () => {
    // Use the actual message title/content instead of technical job ID
    if (job.rawTitle) return job.rawTitle;
    if (job.hasCustomContent) return job.hasCustomContent;
    if (job.templateKey)
      return job.templateKey.replace(/([A-Z])/g, " $1").trim();
    return `Notification Message`;
  };

  const shouldShowProgress = () => {
    const status = job.status?.toLowerCase();
    return (
      ["processing", "sending", "sent", "completed"].includes(status) &&
      job.stats?.total > 0
    );
  };

  const getSuccessRate = () => {
    if (!job.stats?.total || job.stats.total === 0) return 0;
    const delivered = job.stats.delivered || 0;
    return Math.round((delivered / job.stats.total) * 100);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 p-6 border border-neutral-light dark:border-neutral-dark"
    >
      {/* Header with Message Title */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 pr-4">
          <div className="flex items-center gap-2 mb-2">
            <BellIcon className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-text line-clamp-2">
              {getMessageTitle()}
            </h3>
            {job.isPromotional && (
              <span className="text-xs bg-secondary-light text-secondary-dark px-2 py-1 rounded-full font-medium">
                Promotional
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-dark dark:text-neutral-light">
            {getStatusMessage()}
          </p>
          <p className="text-xs text-neutral-dark dark:text-neutral-light opacity-75 mt-1">
            {formatDate(job.createdAt)}
          </p>
        </div>
        <JobStatusChip status={job.status} size="md" />
      </div>

      {/* Message Preview */}
      {job.rawBody && (
        <div className="bg-glass-light dark:bg-glass-dark rounded-lg p-3 mb-4">
          <p className="text-sm text-text line-clamp-2">"{job.rawBody}"</p>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-light rounded-lg">
            <UserGroupIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-xs text-neutral-dark dark:text-neutral-light uppercase tracking-wide">
              Audience
            </p>
            <p className="text-lg font-semibold text-text">
              {formatNumber(job.stats?.total || job.audience?.total || 0)}
            </p>
          </div>
        </div>

        {job.scheduleAt ? (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warning-light rounded-lg">
              <CalendarIcon className="w-4 h-4 text-warning" />
            </div>
            <div>
              <p className="text-xs text-neutral-dark dark:text-neutral-light uppercase tracking-wide">
                Scheduled
              </p>
              <p className="text-sm font-medium text-text">
                {formatDate(job.scheduleAt)}
              </p>
            </div>
          </div>
        ) : shouldShowProgress() ? (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-light rounded-lg">
              <CheckCircleIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-xs text-neutral-dark dark:text-neutral-light uppercase tracking-wide">
                Success Rate
              </p>
              <p className="text-lg font-semibold text-text">
                {getSuccessRate()}%
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* Progress Section - Only show when relevant */}
      {shouldShowProgress() && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-text">
              Delivery Progress
            </span>
            <span className="text-sm text-neutral-dark dark:text-neutral-light">
              {formatNumber(job.stats.sent)} of {formatNumber(job.stats.total)}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-neutral-light dark:bg-neutral-dark rounded-full h-2 mb-3">
            <div
              className="bg-gradient-to-r from-primary to-primary-dark h-2 rounded-full transition-all duration-500"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>

          {/* Engagement Stats - Only show if there's meaningful data */}
          {(job.stats.delivered > 0 ||
            job.stats.opened > 0 ||
            job.stats.clicked > 0) && (
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <CheckCircleIcon className="w-3 h-3 text-success" />
                  <p className="text-xs text-neutral-dark dark:text-neutral-light">
                    Delivered
                  </p>
                </div>
                <p className="text-sm font-semibold text-success">
                  {formatNumber(job.stats.delivered)}
                </p>
              </div>

              {job.stats.opened > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <EyeIcon className="w-3 h-3 text-primary" />
                    <p className="text-xs text-neutral-dark dark:text-neutral-light">
                      Opened
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-primary">
                    {formatNumber(job.stats.opened)}
                  </p>
                </div>
              )}

              {job.stats.clicked > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CursorClickIcon className="w-3 h-3 text-secondary" />
                    <p className="text-xs text-neutral-dark dark:text-neutral-light">
                      Clicked
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-secondary">
                    {formatNumber(job.stats.clicked)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Show failures if any */}
          {job.stats.failed > 0 && (
            <div className="mt-3 p-2 bg-danger-light rounded-lg">
              <div className="flex items-center gap-2">
                <ExclamationCircleIcon className="w-4 h-4 text-danger-dark" />
                <span className="text-sm text-danger-dark">
                  {formatNumber(job.stats.failed)} messages failed to deliver
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-2 border-t border-neutral-light dark:border-neutral-dark">
        <ActionButtons
          buttonsConfig={jobButtonsConfig[job.status?.toLowerCase()] || []}
          onAction={handleAction}
          size="sm"
        />
      </div>
    </motion.div>
  );
};

export default JobCard;
